import { Amount, CURRENCIES } from "@fideus-core/transaction";
import { Temporal } from "temporal-polyfill";
import { z } from "zod/v4";
import type { LoanAgreementTransaction } from "./LoanAgreementTransaction";

export const loanAgreementTypes = ["single", "master"] as const;
export type LoanAgreementType = (typeof loanAgreementTypes)[number];

const serializedAmountSchema = z.object({
  amount: z.number(),
  currency: z.enum(CURRENCIES),
  precision: z.number(),
});

export const serializedLoanAgreementTransactionSchema = z.object({
  id: z.string(),
  loanAgreementId: z.string(),
  type: z.enum(["retrieval", "payback"]),
  amount: serializedAmountSchema,
  date: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type SerializedLoanAgreementTransaction = z.infer<
  typeof serializedLoanAgreementTransactionSchema
>;

export const serializedLoanAgreementSchema = z.object({
  id: z.string(),
  companyId: z.string(),
  name: z.string(),
  type: z.enum(loanAgreementTypes),
  yearlyInterestRate: z.string(),
  amount: serializedAmountSchema,
  transactions: z.array(serializedLoanAgreementTransactionSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type SerializedLoanAgreement = z.infer<
  typeof serializedLoanAgreementSchema
>;

export interface CreateLoanAgreementParams {
  id: string;
  companyId: string;
  name: string;
  type: LoanAgreementType;
  yearlyInterestRate: number;
  amount: Amount;
  transactions?: LoanAgreementTransaction[];
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Calculates the number of days between two dates using the German 30/360 convention.
 * Each month is treated as 30 days, and the year as 360 days.
 */
function days30360(start: Temporal.PlainDate, end: Temporal.PlainDate): number {
  let d1 = start.day;
  let d2 = end.day;

  if (d1 === 31) d1 = 30;
  if (d2 === 31 && d1 >= 30) d2 = 30;

  return (
    (end.year - start.year) * 360 + (end.month - start.month) * 30 + (d2 - d1)
  );
}

export class LoanAgreement {
  private readonly _id: string;
  private readonly _companyId: string;
  private readonly _name: string;
  private readonly _type: LoanAgreementType;
  private readonly _yearlyInterestRate: number;
  private readonly _amount: Amount;
  private readonly _transactions: LoanAgreementTransaction[];
  private readonly _createdAt: Date;
  private readonly _updatedAt: Date;

  private constructor(
    id: string,
    companyId: string,
    name: string,
    type: LoanAgreementType,
    yearlyInterestRate: number,
    amount: Amount,
    transactions: LoanAgreementTransaction[],
    createdAt: Date,
    updatedAt: Date
  ) {
    this._id = id;
    this._companyId = companyId;
    this._name = name;
    this._type = type;
    this._yearlyInterestRate = yearlyInterestRate;
    this._amount = amount;
    this._transactions = transactions;
    this._createdAt = createdAt;
    this._updatedAt = updatedAt;
  }

  static create(params: CreateLoanAgreementParams): LoanAgreement {
    return new LoanAgreement(
      params.id,
      params.companyId,
      params.name,
      params.type,
      params.yearlyInterestRate,
      params.amount,
      params.transactions ?? [],
      params.createdAt,
      params.updatedAt
    );
  }

  static deserialise(data: SerializedLoanAgreement): LoanAgreement {
    return new LoanAgreement(
      data.id,
      data.companyId,
      data.name,
      data.type,
      Number.parseFloat(data.yearlyInterestRate),
      Amount.deserialise(data.amount),
      data.transactions.map((t) => ({
        id: t.id,
        loanAgreementId: t.loanAgreementId,
        type: t.type,
        amount: Amount.deserialise(t.amount),
        date: Temporal.PlainDate.from(t.date),
        createdAt: new Date(t.createdAt),
        updatedAt: new Date(t.updatedAt),
      })),
      new Date(data.createdAt),
      new Date(data.updatedAt)
    );
  }

  static isLoanAgreement(value: unknown): value is LoanAgreement {
    return value instanceof LoanAgreement;
  }

  get id(): string {
    return this._id;
  }
  get companyId(): string {
    return this._companyId;
  }
  get name(): string {
    return this._name;
  }
  get type(): LoanAgreementType {
    return this._type;
  }
  get yearlyInterestRate(): number {
    return this._yearlyInterestRate;
  }
  get amount(): Amount {
    return this._amount;
  }
  get transactions(): LoanAgreementTransaction[] {
    return this._transactions;
  }
  get createdAt(): Date {
    return this._createdAt;
  }
  get updatedAt(): Date {
    return this._updatedAt;
  }

  /**
   * Calculate remaining loan balance at a given date.
   * Both "single" and "master" types start at 0 balance.
   * Retrievals increase the outstanding balance, paybacks decrease it.
   * Only considers transactions on or before `date`.
   */
  getRemainingAmount(date: Temporal.PlainDate): Amount {
    const currency = this._amount.getCurrency();
    return this._transactions
      .filter((t) => Temporal.PlainDate.compare(t.date, date) <= 0)
      .reduce(
        (balance, t) => {
          if (t.type === "retrieval") {
            return balance.add(t.amount);
          }
          return balance.subtract(t.amount);
        },
        Amount.fromAmountAndCurrency(0, currency)
      );
  }

  /**
   * Calculate accrued interest from the earliest transaction up to `date`
   * using the German 30/360 convention (each month = 30 days, year = 360 days).
   *
   * Walks through sorted transactions, computing interest on the outstanding
   * balance for each period between transactions.
   */
  getAccruedInterest(date: Temporal.PlainDate): Amount {
    const currency = this._amount.getCurrency();
    const sorted = this._transactions
      .filter((t) => Temporal.PlainDate.compare(t.date, date) <= 0)
      .sort((a, b) => Temporal.PlainDate.compare(a.date, b.date));

    if (sorted.length === 0) {
      return Amount.fromAmountAndCurrency(0, currency);
    }

    let totalInterestCents = 0;
    let balanceCents = 0;
    let prevDate: Temporal.PlainDate | undefined;

    for (const transaction of sorted) {
      if (prevDate !== undefined) {
        const days = days30360(prevDate, transaction.date);
        totalInterestCents +=
          (balanceCents * this._yearlyInterestRate * days) / 360;
      }

      if (transaction.type === "retrieval") {
        balanceCents += transaction.amount.getAmount();
      } else {
        balanceCents -= transaction.amount.getAmount();
      }

      prevDate = transaction.date;
    }

    // Interest from last transaction to the target date
    if (prevDate !== undefined) {
      const remainingDays = days30360(prevDate, date);
      if (remainingDays > 0) {
        totalInterestCents +=
          (balanceCents * this._yearlyInterestRate * remainingDays) / 360;
      }
    }

    return Amount.fromAmountAndCurrency(
      Math.round(totalInterestCents),
      currency
    );
  }

  serialise(): SerializedLoanAgreement {
    return {
      id: this._id,
      companyId: this._companyId,
      name: this._name,
      type: this._type,
      yearlyInterestRate: this._yearlyInterestRate.toString(),
      amount: this._amount.toJSON(),
      transactions: this._transactions.map((t) => ({
        id: t.id,
        loanAgreementId: t.loanAgreementId,
        type: t.type,
        amount: t.amount.toJSON(),
        date: t.date.toString(),
        createdAt: t.createdAt.toISOString(),
        updatedAt: t.updatedAt.toISOString(),
      })),
      createdAt: this._createdAt.toISOString(),
      updatedAt: this._updatedAt.toISOString(),
    };
  }

  toJSON(): SerializedLoanAgreement {
    return this.serialise();
  }
}
